import {
  Box,
  Container,
  Flex,
  FooterAddress,
  FooterCopyright,
  FooterCountrySelector,
  FooterLegalLinks,
  FooterLinks,
  FooterPaymentMethods,
  FooterQmId,
  FooterRegistered,
  FooterSocialLinks,
  Logo,
  ThemeWrapper,
  ValueProps,
} from "@theme/components";

import type { ThemeUIStyleObject } from "@theme/components";
import type { FooterDataProps } from "@theme/types";

export const Footer = ({
  footerData,
}: {
  footerData: FooterDataProps;
  sx?: ThemeUIStyleObject;
}) => {
  const {
    footerLinks,
    footerLegalLinks,
    showFooterAddress,
    EmailMarketingSignUpForm,
    isBlog,
    supportEmail,
    valuePropModule,
  } = footerData;

  return (
    <ThemeWrapper theme="DARK">
      <Flex
        as="footer"
        sx={{
          backgroundColor: "black",
          color: "white",
          flexDirection: "column",
          width: "full",
          position: "relative",
          px: 0,
        }}
      >
        {valuePropModule && (
          <ValueProps
            theme="Dark"
            bgColor={{ key: "backgroundPrimary" }}
            valueProps={valuePropModule.valueProp}
            sx={{
              borderBottomColor: "grey500",
              borderBottomStyle: "solid",
              borderBottomWidth: [0, null, "default"],
            }}
          />
        )}
        <Container variant="wide">
          <Flex
            sx={{
              flexWrap: "nowrap",
              flexDirection: "column",
              borderBottomColor: "grey500",
              borderBottomStyle: "solid",
              borderBottomWidth: ["default", null, "0"],
              width: "full",
              pt: [0, null, "s900"],
              mb: ["s700", null, "s1000"],
            }}
          >
            {/* FOOTER LINKS */}
            <FooterLinks footerGroup={footerLinks} isBlog={isBlog} />

            <Flex
              sx={{
                flexDirection: ["column", null, "row"],
                margin: "auto",
                width: "full",
              }}
            >
              <Flex
                sx={{
                  flexDirection: "column",
                  gap: "s800",
                  mb: ["s800", null, 0],
                  flex: ["1 1 100%", null, "0 0 50%"],
                  pr: [0, null, "s900"],
                }}
              >
                <FooterPaymentMethods footerData={footerData} />
                {showFooterAddress && <FooterAddress supportEmail={supportEmail} />}
                {/* SOCIAL LINKS */}
                <FooterSocialLinks footerData={footerData} />
              </Flex>

              {/* CONTACT BOX */}
              {EmailMarketingSignUpForm && (
                <Flex
                  sx={{
                    gap: "s400",
                    flexDirection: "column",
                    flex: ["1 1 100%", null, "0 0 50%"],
                    alignItems: ["center", null, "flex-start"],
                    pb: ["s700", null, 0],
                  }}
                >
                  <EmailMarketingSignUpForm heading={footerData.headers.footerEmailTitle} />
                </Flex>
              )}
            </Flex>
          </Flex>
        </Container>

        <Container variant="wide">
          <Flex
            sx={{
              justifyContent: ["center", null, null, "space-between"],
              flexWrap: "wrap",
              pt: ["s800", null, null, 0],
              pb: ["s800", null, null, "s1000"],
              width: "full",
            }}
          >
            <Flex
              variant="wideNoGutters"
              sx={{
                flexDirection: ["column", null, "row"],
                justifyContent: ["center", null, "space-between"],
                alignItems: "center",
                textAlign: ["center", null, "left"],
              }}
            >
              <Box sx={{ width: "332px", flexGrow: 0, flexShrink: 0, pb: ["s600", null, 0] }}>
                <Logo sx={{ maxWidth: ["100px", null, "150px"] }} />
              </Box>
              <Flex
                sx={{
                  flexDirection: "column",
                  justifyContent: ["center", null, "flex-start"],
                  flexBasis: "100%",
                  flexGrow: 1,
                  flexShrink: 1,
                  rowGap: "s200",
                }}
              >
                <Flex
                  sx={{
                    alignItems: ["center", null, "flex-start"],
                    flexDirection: ["column", null, null, "row"],
                    columnGap: "s700",
                    rowGap: "s200",
                  }}
                >
                  <FooterLegalLinks legalLinks={footerLegalLinks} isBlog={isBlog} />
                  <FooterCopyright
                    currentYear={new Date().getFullYear()}
                    allRightsReserved={footerData.headers.footerAllRightsReserved}
                  />
                </Flex>
                <FooterRegistered registeredSymbol={footerData.headers.footerRegisteredSymbol} />
              </Flex>
            </Flex>
          </Flex>
          <FooterCountrySelector countries={footerData.countries} isBlog={footerData.isBlog} />
          <FooterQmId />
        </Container>
      </Flex>
    </ThemeWrapper>
  );
};
